import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner2 } from 'components';
import { useQueryBuilder, FilterRule } from 'components/Filter/useQueryBuilder';
import {
  IParts,
  useGetPartsQuery,
  useRedoPartMutation,
} from 'thunks/RTKQuery/parts';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { UserLayout } from 'components/Layout';
import { Row, Col, Container } from 'react-bootstrap';
import { PartsView, ISortBy } from 'pages/Shared/Parts';
import { isFetchBaseQueryError } from 'utils/helpers';
import { toastSlice } from 'slices';

const defaultSortBy: ISortBy = { dataField: 'name', order: 'asc' };

export const MillPartsView = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<ISortBy>(defaultSortBy);
  const [parts, setParts] = useState<IParts[]>([]);
  const [filters, setFilters] = useState<FilterRule[]>([]);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [errorString, setErrorString] = useState('');

  const { params } = useQueryBuilder(filters);

  const { data: authData } = useAppSelector((state) => state.auth);
  const user = authData?.user;
  const userSettings: any = user?.userSettings;
  const { data, isLoading, isFetching } = useGetPartsQuery(
    {
      pageSize,
      pageNumber: page,
      sort: `${sortBy.dataField}:${sortBy.order}`,
      ...params,
    },
    { skip: !userSettings?.pageSize }
  );

  useEffect(() => {
    setPageSize(+userSettings?.pageSize || 20);
  }, [userSettings]);

  useEffect(() => {
    if (data?.data) {
      setParts(data.data);
    }
  }, [data]);

  useEffect(() => {
    setPage(1); // Reset to first page whenever filters change
  }, [filters]);

  const [
    redoPart,
    { isLoading: isRedoing, error: redoError, status: redoStatus },
  ] = useRedoPartMutation();

  useEffect(() => {
    if (redoStatus === 'fulfilled' && !redoError) {
      dispatch(
        toastSlice.actions.show({
          title: t('partsView.redoSuccess'),
          message: t('partsView.redoSuccessMessage'),
        })
      );
    }
  }, [redoStatus]);

  const handleRedo = (id: number) => {
    redoPart({ id, millId: null });
  };
  useEffect(() => {
    if (redoError && isFetchBaseQueryError(redoError)) {
      const msg = redoError as any;
      setErrorString(`${msg.data.message.errors}`);
    }
  }, [redoError]);

  useEffect(() => {
    if (errorString !== '') {
      dispatch(
        toastSlice.actions.show({
          title: t('contract.error'),
          message: errorString,
        })
      );
    }
    setErrorString('');
  }, [errorString]);

  return (
    <div>
      <UserLayout mainClasses="page page-orders">
        <div className="d-flex flex-column w-100 py-60 py-md-70 pl-md-126 px-20 pr-md-54">
          <Container fluid>
            <Spinner2 show={isLoading || isFetching || isRedoing} />
            <Row>
              <Col>
                <h1 className="fs-44 mb-10">{t('partsView.heading')}</h1>
              </Col>
            </Row>

            <PartsView
              parts={parts}
              count={data?.count || 0}
              page={page}
              sizePerPage={pageSize}
              setPage={setPage}
              setSortBy={setSortBy}
              sortBy={sortBy}
              filters={filters}
              setFilters={setFilters}
              redoPart={handleRedo}
            />
          </Container>
        </div>
      </UserLayout>
    </div>
  );
};

export default MillPartsView;
